import { ElementRef, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Subject,
  catchError,
  combineLatest,
  finalize,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  Subscription,
  take,
  throwError,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { BaseService } from 'src/app/core/services/base.httpService';
import { URLConstants } from '../utility/url-constants';
import {
  IDropdownResponse,
  ILookupDropdownResponse,
  INotesDetail,
  INotesSectionData,
  SectionObject,
  SvgIconProp,
  TableColumns,
} from '../models/dynamic.model';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { buildAdvanceSearchDisplayName } from '../components/advanced-search/advance-search-display-name.util';
import {
  ActionIcons,
  allowedKeys,
  BasicSearchPlaceholder,
  dateTimeFormat,
  HttpSignatures,
  LabelsAndPlaceHolders,
  MasterLookup,
  ModalPopupMessages,
  ModeIcons,
  NotesType,
  pageType,
  ScrollData,
  tabKeyboardEvent,
  validationErrorMessages,
  DataTypes,
  lookupcontrolNames,
  popupWidth,
  DropdownSelectionValues,
} from '../utility/constants';
import {
  AddCustomerNotes,
  AddIBCNotes,
  DeleteCustomerNotes,
  DeleteIBCNotes,
  UpdateCustomerNotes,
  UpdateIBCNotes,
} from '../components/notes/store/notes.action';
import { Store } from '@ngxs/store';
import { HttpClient } from '@angular/common/http';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { RowSelectionChangeParams } from 'src/app/features/model/manifest.model';
import { DatePipe } from '@angular/common';
import { ValidatorPattern } from '../utility/validators.pattern';
import { AttachmentsHeaders, NotesHeaders } from '../configs/common-configs';
import { PrintService } from './print.service';
import { DuplicatePopupResult } from 'src/app/features/components/accounts-payable/accounts-payable.model';
import {
  CustomsCityProcessClearanceRequest,
  CustomsCityProcessClearanceResponse,
  CustomsCityValidateClearanceSelectionRequest,
  CustomsCityValidateClearanceSelectionResponse,
  CustomsCityValidateCustomsCityClearanceResponse,
} from '../models/customs-city-clearance.model';
import {
  CustomsCityClearanceStatusCodes,
  CustomsCityValidateChannelStatusCodes,
  parseCustomsCityClearanceResponse,
  parseCustomsCityValidateCustomsCityClearanceResponse,
} from '../utility/customs-city-clearance.util';

export type ValidFieldValue = string | number | null | undefined;

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  public savedBagData = new BehaviorSubject<any>({});
  public clearSelectionInputValue = new BehaviorSubject<any>('');
  public formPatchback = new BehaviorSubject<any>('');
  public criteriaInvalid = new BehaviorSubject<any>(false);
  public miscellaneousInvalid = new BehaviorSubject<any>(false);
  public getPrevSelectedMode = new BehaviorSubject<string>('');
  public getPrevPageMode = new BehaviorSubject<any>('');
  public pieceSelectedData = new BehaviorSubject<any>([]);
  public isSelectedDataForBulkBagging = new BehaviorSubject<any>([]);
  public isPopupOpen = new BehaviorSubject<boolean>(false);
  public delete = new BehaviorSubject<any>([]);
  public isCheckForMode = new BehaviorSubject<any>('');
  public isUFOAccountRestrictedEdit = new BehaviorSubject<boolean>(false);
  public isBulkEditViewMode = new BehaviorSubject<boolean>(false);
  public isCheckDataValues = new BehaviorSubject<any>('');
  public isBagPageMode = new BehaviorSubject<any>('');
  public checkAccordionDirtyData = new BehaviorSubject<boolean>(true);
  public checkValidDirtyData = new BehaviorSubject<boolean>(true);
  public checkAdvanceSearchFieldDirty = new BehaviorSubject<boolean>(false);
  public advanceSearchSubmitAttempt = new Subject<void>();
  public routingFormValidity = new BehaviorSubject<boolean>(true);
  public checkAccordionPieceDirtyData = new BehaviorSubject<boolean>(true);
  public checkAccordionCommodityDirtyData = new BehaviorSubject<boolean>(true);
  public checkOverlayOpened = new BehaviorSubject<boolean>(false);
  public overlayRefData = new BehaviorSubject<any>('');
  public mockFlag = false;
  public idData = new BehaviorSubject<any>('');
  public isMenuExpanded = new BehaviorSubject<boolean>(false);
  public getManifestPreviousMode = new BehaviorSubject<string>('');
  public isPreviousPage = new BehaviorSubject<string>('');
  public isflightDelete = new BehaviorSubject<string>('');
  public patchSearchText = new BehaviorSubject<string>('');
  private preventFurtherTypingSubscription: Subscription | null = null;
  private $subscription: Subscription | undefined;
  public paginationData = new BehaviorSubject<any>({});
  public bagPaginationData = new BehaviorSubject<any>({});
  public paginationData$ = this.paginationData.asObservable();
  public getHawbIdFromHawbInformation = new BehaviorSubject<any>(null);
  public getMawbIdFromMawbInformation = new BehaviorSubject<any>(null);
  public checkForCommodityAttachmentChanges = new BehaviorSubject<any>(false);
  public isBagFromPage = new BehaviorSubject<string>('');
  public isSetMasterShipmentValue = new BehaviorSubject<any>({});
  public isSetEventsDataValue = new BehaviorSubject<any>({});
  public isListAllResultsData = new BehaviorSubject<boolean>(false);
  public ishitAPIAfterCBPClearance = new BehaviorSubject<boolean>(false);
  public isLoadMoreEventsTriggered = new BehaviorSubject<any>('');
  public isSelectedDataForBulkMenifest = new BehaviorSubject<any>([]);
  public isSelectedDataForBulkMawb = new BehaviorSubject<any>([]);
  private readonly _lastUsedPayload: any = new BehaviorSubject<any>(null);
  public isSelectedDataForBagScanShipment = new BehaviorSubject<any>([]);
  public locationWidgetUpdate = new BehaviorSubject<boolean>(false);
  public selectedGatewayBaggingStatus = new BehaviorSubject<string>(DropdownSelectionValues.Select);
  public selectedCbpHeldFilter = new BehaviorSubject<'' | 'true' | 'false'>('');
  private selectedIsShipmentRouted = new BehaviorSubject<
    '' | 'true' | 'false'
  >('');
  private specialSearchCheckboxFilters = new BehaviorSubject<
    Record<string, '' | 'true' | 'false'>
  >({});

  private readonly viewDeletedAttachmentIdKeys = new Set<string>();
  private attachmentTypeLookup$: Observable<any> | null = null;
  private deliveryTermsLookup$?: Observable<IDropdownResponse[]>;

  constructor(
    public baseService: BaseService,
    private readonly store: Store,
    private readonly http: HttpClient,
    private readonly datePipe: DatePipe,
    private readonly printService: PrintService
  ) { }

  public releasePreventFurtherTyping(): void {
    this.preventFurtherTypingSubscription?.unsubscribe();
    this.preventFurtherTypingSubscription = null;
  }

  public markAttachmentIdsDeletedOnServer(ids: ValidFieldValue[]): void {
    for (const id of ids) {
      if (this.isValidField(id)) {
        this.viewDeletedAttachmentIdKeys.add(String(id));
      }
    }
  }

  public clearViewDeletedAttachmentIds(): void {
    this.viewDeletedAttachmentIdKeys.clear();
  }

  public isAttachmentIdDeletedOnServer(id: ValidFieldValue): boolean {
    if (!this.isValidField(id)) return false;
    return this.viewDeletedAttachmentIdKeys.has(String(id));
  }

  public setBagFromPage(data: string) {
    this.isBagFromPage.next(data);
  }

  public isDataSaved(data: any) {
    this.savedBagData.next(data);
  }

  public isCriteriaData(data: boolean) {
    this.criteriaInvalid.next(data);
  }

  public makeMiscellaneousDirty(data: boolean) {
    this.miscellaneousInvalid.next(data);
  }

  public setMode(data: any) {
    this.isCheckForMode.next(data);
  }

  public setUFOAccountRestrictedEdit(isRestricted: boolean) {
    this.isUFOAccountRestrictedEdit.next(isRestricted);
  }

  public setBulkEditViewMode(isBulkEditView: boolean) {
    this.isBulkEditViewMode.next(isBulkEditView);
  }

  public disableAllIconsInBulkEditViewMode(
    pageMode: string,
    icons: SvgIconProp[]
  ): SvgIconProp[] | null {
    const isBulkEditViewMode = this.isBulkEditViewMode.value;
    const isBulkEditContext = this.isBulkEditContextFromUrl();
    if (pageMode === ModeIcons.view && isBulkEditViewMode && isBulkEditContext) {
      const clonedIcons = structuredClone<SvgIconProp[]>(icons);
      return clonedIcons.map((icon: SvgIconProp) => {
        icon.isDisabled = true;
        return icon;
      });
    }
    return null;
  }

  private isBulkEditContextFromUrl(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    return mode === ActionIcons.bulkEdit;
  }

  public setBagPageMode(data: string) {
    this.isBagPageMode.next(data);
  }

  public updateLocationWidget(updateLocation: boolean) {
    this.locationWidgetUpdate.next(updateLocation);
  }

  public getBagPageMode(data: string) {
    this.getPrevPageMode.next(data);
  }

  public getMode(data: string) {
    this.getPrevSelectedMode.next(data);
  }

  public getModeForManifest(data: string) {
    this.getManifestPreviousMode.next(data);
  }

  public isanyPopupOpen(data: boolean) {
    this.isPopupOpen.next(data);
  }

  public setMasterShipmentValue(masterShipmentData: any) {
    this.isSetMasterShipmentValue.next(masterShipmentData);
  }

  public isValidField(field: ValidFieldValue): boolean {
    return field !== '' && field != null;
  }

  public hasClearableValue(
    control: AbstractControl | null | undefined,
    multiple = false
  ): boolean {
    if (!control || control.disabled) {
      return false;
    }
    const value = control.value;
    if (multiple) {
      return Array.isArray(value) && value.length > 0;
    }
    return this.isValidField(value);
  }

  public clearControlValue(
    control: AbstractControl | null | undefined,
    options?: { multiple?: boolean; emptyValue?: unknown }
  ): void {
    if (!control || control.disabled) {
      return;
    }
    this.releasePreventFurtherTyping();
    const multiple = options?.multiple ?? false;
    const defaultEmpty = multiple ? [] : '';
    const emptyValue =
      options?.emptyValue === undefined ? defaultEmpty : options.emptyValue;
    control.setValue(emptyValue, { emitEvent: true });
    control.markAsDirty();
  }

  public clearCompanionControls(
    form: AbstractControl,
    ...companionControlNames: Array<string | null | undefined>
  ): void {
    const formGroup = form as FormGroup;
    for (const name of companionControlNames) {
      if (!name) {
        continue;
      }
      formGroup.get(name)?.setValue(null, { emitEvent: false });
    }
  }

  public isNotValidField(field: ValidFieldValue): boolean {
    return field === null || field === undefined || field === '';
  }
  public getCountriesData(): Observable<any> {
    const url = URLConstants.getCountryDetails;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }

  public getStateData(data: any): Observable<any> {
    const url = URLConstants.getStateDetails + data;
    return this.baseService.initiateApiRequest('GET', url, data, 'json');
  }

  public bagExistsForBulkBagging(bagData: string) {
    const url = `${URLConstants.validateBagExists}/${bagData}`;
    return this.baseService.initiateApiRequest('GET', url, bagData, 'json');
  }

  public generateNumber(data: any) {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.generateNumber,
      data,
      'json'
    );
  }

  public createBagForBulkBagging(bulkBagData: any) {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Put'],
      URLConstants.createBagForBulkBagging,
      bulkBagData,
      'json'
    );
  }

  public selectedDataForBulkBagging(data: any) {
    this.isSelectedDataForBulkBagging.next(data);
  }

  public pieceDetails(data: any) {
    this.pieceSelectedData.next(data);
  }

  public getMasterLookUp(data: string): Observable<any> {
    const url = URLConstants.requestType + data;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }

  public getAttachmentTypeLookup(): Observable<any> {
    if (!this.attachmentTypeLookup$) {
      this.attachmentTypeLookup$ = this.getMasterLookUp(
        MasterLookup.attachmentType
      ).pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.attachmentTypeLookup$;
  }

  public getDeliveryTermsLookup(): Observable<IDropdownResponse[]> {
    if (!this.deliveryTermsLookup$) {
      this.deliveryTermsLookup$ = this.getMasterLookups(
        MasterLookup.deliveryTerms
      ).pipe(
        map((response: ILookupDropdownResponse) => response?.data ?? []),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.deliveryTermsLookup$;
  }

  public clearAttachmentTypeCache(): void {
    this.attachmentTypeLookup$ = null;
  }

  public fetchMasterAccounts(): Observable<any> {
    return this.baseService.initiateApiRequest(
      'GET',
      URLConstants.getAccountDetails,
      '',
      'json'
    );
  }

  public getMasterLookups(data: string): Observable<any> {
    const url = URLConstants.masterLookupType + data;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }


  public fetchAccountById(accountId: number): Observable<any> {
    const url = URLConstants.fetchAccountById + accountId;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }

  public getUserProfile(): Observable<any> {
    return this.baseService.initiateApiRequest('GET', URLConstants.getUserProfileDetails, '', 'json');
  }

  public sendARInvoiceViaEmail(payload: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.sendARInvoiceViaEmail,
      payload,
      'json'
    );
  }

  public getInvoiceNotificationEmails(accountData: any): string[] {
    const emails = accountData?.account?.governmentInformation?.selectEmailsForInvoiceNotification;
    return Array.isArray(emails) && emails.length > 0 ? emails : [];
  }

  public getRateScalesBasedOnAgents(data: string): Observable<any> {
    const url = URLConstants.getRateScalesBasedOnAgents + data;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }

  public getFacilitiesUsingCountry(countryCode: string): Observable<any> {
    const url = URLConstants.facilitiesUsingCountry + countryCode;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }

  public getIconsForInfoMode(
    mode: string,
    hawbInfoHeaderIcons: SvgIconProp[]
  ): SvgIconProp[] {
    const filtered: SvgIconProp[] = [];
    if (mode === 'View') {
      hawbInfoHeaderIcons.forEach((val: SvgIconProp) => {
        if (val.order === 2 || val.order === 4) {
          filtered.push(val);
        }
      });
      filtered.forEach((icon: SvgIconProp) => {
        if (icon.order === 2 || icon.order === 4) {
          icon.isDisabled = false;
        }
      });
    }
    if (mode === 'Edit') {
      hawbInfoHeaderIcons.forEach((val: SvgIconProp) => {
        if (val.order === 1 || val.order === 3 || val.order === 4) {
          filtered.push(val);
        }
      });
      filtered.forEach((icon: SvgIconProp) => {
        if (icon.order === 1 || icon.order === 3 || icon.order === 4) {
          icon.isDisabled = false;
        }
      });
    }
    if (mode === 'MainView') {
      hawbInfoHeaderIcons.forEach((val: SvgIconProp) => {
        if (val.order === 2 || val.order === 4) {
          filtered.push(val);
        }
      });
      filtered.forEach((icon: SvgIconProp) => {
        if (icon.order === 2 || icon.order === 4) {
          icon.isDisabled = true;
        }
      });
    }
    return filtered;
  }

  public checkPristineAccordionData(data: any) {
    this.checkAccordionDirtyData.next(data);
  }

  public checkValidAccordionData(data: any) {
    this.checkValidDirtyData.next(data);
  }

  public checkPristinePieceAccordionData(data: any) {
    this.checkAccordionPieceDirtyData.next(data);
  }

  public setOverlayRefData(data: any) {
    this.overlayRefData.next(data);
  }

  public getDataId(data: any) {
    this.idData.next(data);
  }

  /* For Checking Control Error to show tooltip */
  public checkControlError(formName: FormGroup, controlName: string) {
    const form = formName;
    return form.get(controlName)?.touched && form.get(controlName)?.errors;
  }

  /* For setting width, event and enabling of tooltips for text fields */
  public setErrorTooltipData(
    event: MouseEvent,
    fieldName: string,
    setWidth: any,
    setTooltipEvent: any,
    setErrorTooltip: any,
    reduceWidth?: number,
    isFrom?: string
  ) {
    setWidth[fieldName] = this.getControlWidth(event, reduceWidth, isFrom);
    setTooltipEvent[fieldName] = event;
    setErrorTooltip[fieldName] = true;
  }

  /* For clearing of tooltips for text fields */
  public closeErrorTooltip(controlName: string, setErrorTooltip: any) {
    setErrorTooltip[controlName] = false;
  }

  /* For getting width of text fields to show tooltip */
  public getControlWidth(
    event: MouseEvent,
    reduceWidth?: number,
    isFrom?: string
  ) {
    const target = event.target as HTMLElement;
    const parentElement =
      target.parentElement?.parentNode?.parentNode?.parentNode?.parentElement;
    if (reduceWidth) {
      return (parentElement?.offsetWidth ?? 0) - reduceWidth + 'px';
    } else {
      return (
        (isFrom === 'address-name' ? 210 : parentElement?.offsetWidth) + 'px'
      );
    }
  }

  /* For setting width of text fields to show tooltip after sidebar expanded/collapsed */
  public setModifiedTooltipWidth(event: any, tooltip: any, width: any) {
    const openedControls = Object.keys(tooltip).filter(
      key => tooltip[key] === true
    );
    if (openedControls.length) {
      openedControls.forEach((control: string) => {
        width[control] = this.getControlWidth(event[control]);
      });
      return width;
    } else {
      return {};
    }
  }

  public checkPristineCommodityAccordionData(data: any) {
    this.checkAccordionCommodityDirtyData.next(data);
  }

  //notes tooltip for username and facility
  public notesTooltip(tooltipInfo: any): string {
    if (!tooltipInfo) return '';
    const userName = tooltipInfo.userName
      ? tooltipInfo.userName
      : tooltipInfo.type === 'customer'
        ? 'Customer'
        : 'IBC';
    const facility = tooltipInfo.facility || '';

    let tooltipContent = '';
    tooltipContent += `${userName}\n${facility}`;
    return tooltipContent;
  }

  // custom tooltip overlay
  public setOverlayOpen(data: any) {
    this.checkOverlayOpened.next(data);
  }

  // get notes by screen - starts
  public filterNotesDatabyScreen(
    data: INotesSectionData,
    isFromScreen: string
  ) {
    const ibcBag = data.ibc.filter(
      (note: any) =>
        note.isFromScreen === isFromScreen && note.action !== 'delete'
    );
    const customerBag = data.customer.filter(
      (note: any) =>
        note.isFromScreen === isFromScreen && note.action !== 'delete'
    );
    const count = customerBag.length + ibcBag.length;
    return count > 0 ? `(${customerBag.length + ibcBag.length})` : '';
  }
  // get notes by screen - ends

  //update notes after save for commodity
  public updateChildScreenNotes(pageData: any) {
    const ibcNotes = pageData.notes.ibc;
    const customerNotes = pageData.notes.customer;
    if (ibcNotes && ibcNotes.length > 0) {
      ibcNotes.forEach((ibcNote: INotesDetail) => {
        this.store.dispatch(new UpdateIBCNotes(ibcNote));
      });
    }
    if (customerNotes && customerNotes.length > 0) {
      customerNotes.forEach((customerNote: INotesDetail) => {
        this.store.dispatch(new UpdateCustomerNotes(customerNote));
      });
    }
  }

  // remove unsaved notes from commodity information
  public removeUnsavedNotes(dataForRemoval: any[]) {
    dataForRemoval.forEach((note: INotesDetail) => {
      if (!note.isSaved) {
        note.type === NotesType.ibc
          ? this.store.dispatch(new DeleteIBCNotes(note))
          : this.store.dispatch(new DeleteCustomerNotes(note));
      }
    });
  }

  //update notes data to store - starts
  public updateNotes(data: any, moduleName: string, subModuleName: string) {
    const mainNotes = { ibc: data.notes.ibc, customer: data.notes.customer };
    this.dispatchNotesForStore(mainNotes, moduleName);
    if (subModuleName) {
      this.dispatchNotesForStore(
        data.commodityList ? data.commodityList : data.flights,
        subModuleName
      );
    }
  }

  public dispatchNotesForStore(incomingData: any, isFrom: string) {
    const allIBCNotes: any[] = [];
    const allCustomerNotes: any[] = [];
    if (
      isFrom === pageType.bag ||
      isFrom === pageType.hawb ||
      isFrom === pageType.manifest ||
      isFrom === pageType.mawb
    ) {
      this.getIbcCustomerNotes(
        incomingData,
        null,
        allIBCNotes,
        allCustomerNotes,
        isFrom,
        true
      );
    } else {
      if (
        isFrom === pageType.commodity ||
        isFrom === pageType.bagShipment ||
        isFrom === pageType.mawbFlight
      ) {
        this.removeNotesForScreen(isFrom);
      }
      this.notesDataIteration(
        incomingData,
        isFrom,
        allIBCNotes,
        allCustomerNotes
      );
    }
    this.updateNotesStore(allIBCNotes, allCustomerNotes);
  }

  private notesDataIteration(
    incomingData: any,
    isFrom: string,
    allIBCNotes: any[],
    allCustomerNotes: any[]
  ) {
    incomingData?.forEach((item: any) => {
      let notesId;
      if (isFrom === pageType.bagShipment) {
        notesId = item.shipment.id;
      } else if (isFrom === pageType.mawbFlight) {
        notesId = item.flightId;
      } else {
        notesId = item.commodity.id;
      }
      const notesData = item.notes;
      this.getIbcCustomerNotes(
        notesData,
        notesId,
        allIBCNotes,
        allCustomerNotes,
        isFrom,
        true
      );
    });
  }

  private getIbcCustomerNotes(
    notesData: any,
    notesId: any,
    allIBCNotes: any[],
    allCustomerNotes: any[],
    isFrom: string,
    isSavedData: boolean
  ) {
    if (notesData?.ibc) {
      allIBCNotes.push(
        ...this.processNotes(
          notesData.ibc,
          isFrom,
          notesId,
          NotesType.ibc,
          isSavedData
        )
      );
    }
    if (notesData?.customer) {
      allCustomerNotes.push(
        ...this.processNotes(
          notesData.customer,
          isFrom,
          notesId,
          NotesType.customer,
          isSavedData
        )
      );
    }
  }

  private processNotes(
    notes: INotesDetail[],
    isFromScreen: string,
    notesId: any,
    type: string,
    isSavedData: boolean
  ) {
    return notes
      .filter((note: INotesDetail) => this.isPersistableNote(note))
      .map((note: INotesDetail) => ({
        ...note,
        isFromScreen,
        notesId,
        source: 'API',
        action: 'c',
        type,
        isEditable: false,
        isDisable: false,
        isSaved: isSavedData,
      }));
  }

  /** Persist only real notes (must have comments). Event rows like publish `pb` have no comments. */
  public isPersistableNote(note: INotesDetail | null | undefined): boolean {
    return !!note && typeof note.comments === 'string';
  }

  public getNoteIdentityKey(note: INotesDetail): string {
    return [
      note.type ?? '',
      note.isFromScreen ?? '',
      note.notesId ?? '',
      note.id ?? '',
    ].join('|');
  }

  public deduplicateNotesList(notes: INotesDetail[]): INotesDetail[] {
    const seen = new Set<string>();
    return notes.filter(note => {
      const key = this.getNoteIdentityKey(note);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private getNotesStateSnapshot(): INotesSectionData {
    return (
      this.store.selectSnapshot((state: { notes: INotesSectionData }) => state.notes) ?? {
        ibc: [],
        customer: [],
      }
    );
  }

  private removeNotesForScreen(screen: string): void {
    const state = this.getNotesStateSnapshot();
    state.ibc
      .filter(note => note.isFromScreen === screen)
      .forEach(note => this.store.dispatch(new DeleteIBCNotes(note)));
    state.customer
      .filter(note => note.isFromScreen === screen)
      .forEach(note => this.store.dispatch(new DeleteCustomerNotes(note)));
  }

  private updateNotesStore(allIBCNotes: any[], allCustomerNotes: any[]) {
    const state = this.getNotesStateSnapshot();
    const existingIbcKeys = new Set(state.ibc.map(note => this.getNoteIdentityKey(note)));
    const existingCustomerKeys = new Set(
      state.customer.map(note => this.getNoteIdentityKey(note))
    );

    if (allIBCNotes?.length > 0) {
      allIBCNotes.forEach(ibcNote => {
        const key = this.getNoteIdentityKey(ibcNote);
        if (existingIbcKeys.has(key)) {
          this.store.dispatch(new UpdateIBCNotes(ibcNote));
        } else {
          this.store.dispatch(new AddIBCNotes(ibcNote));
          existingIbcKeys.add(key);
        }
      });
    }
    if (allCustomerNotes?.length > 0) {
      allCustomerNotes.forEach(customerNote => {
        const key = this.getNoteIdentityKey(customerNote);
        if (existingCustomerKeys.has(key)) {
          this.store.dispatch(new UpdateCustomerNotes(customerNote));
        } else {
          this.store.dispatch(new AddCustomerNotes(customerNote));
          existingCustomerKeys.add(key);
        }
      });
    }
  }
  //update notes data to store - ends

  //remove unmodified notes - starts
  /**
   * Previously stripped `source:'API' + action:'c'` notes from the save payload.
   * Core module updates treat notes as a replace set — omitting existing notes
   * deleted them in DB (count dropped after full-page save; F5 confirmed wipe).
   * Keep the full arrays so existing + newly added notes are all persisted.
   */
  public noModificationOnNotes(notesData: any) {
    if (!Array.isArray(notesData)) {
      return;
    }
    // Intentionally no-op: do not remove existing API notes from the payload.
  }
  //remove unmodified notes - ends

  public attachmentSignedUrl(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'PUT',
      URLConstants.signedurl,
      data
    );
  }

  public deleteAttachment(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.deleteAttachment,
      data
    );
  }

  public AddAttachmentData(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.addAttachment,
      data,
      'json'
    );
  }

  public attachmentStoredSignedUrl(
    data: any,
    fileName: any,
    reportProgress = false
  ): Observable<any> {
    return this.baseService.uploadFile(data.url, fileName, reportProgress);
  }

  // get side bar expansion value to make tooltip reposition
  public getMenuExpanded(data: any) {
    this.isMenuExpanded.next(data);
  }

  // Comparison Error Logic for origin-destination and min-max values
  public comparisonError(
    value1: any,
    value2: any,
    controlName1: string,
    isFromControlName: string,
    isTextComparison: boolean,
    formGroup: FormGroup, // Pass the form group as a parameter
    controlName2: string // The second control's name
  ) {
    const isBothValid = this.isValidField(value1) && this.isValidField(value2);
    const validCheck = isTextComparison
      ? isBothValid
      : isBothValid && value1 !== 0 && value2 !== 0;

    let condition = false;

    if (isTextComparison) {
      condition = value1.toLowerCase() === value2.toLowerCase();
    } else {
      condition = value1 > value2;
    }

    if (validCheck && condition) {
      if (isFromControlName === controlName1) {
        formGroup.get(controlName1)?.setErrors({ comparisonError: true });
        this.clearFieldError(
          formGroup,
          controlName2,
          MasterLookup.comparisonError
        );
        return {
          control1Error: true,
          control2Error: false,
        };
      } else {
        formGroup.get(controlName2)?.setErrors({ comparisonError: true });
        this.clearFieldError(
          formGroup,
          controlName1,
          MasterLookup.comparisonError
        );
        return {
          control1Error: false,
          control2Error: true,
        };
      }
    } else {
      this.clearFieldError(
        formGroup,
        controlName1,
        MasterLookup.comparisonError
      );
      this.clearFieldError(
        formGroup,
        controlName2,
        MasterLookup.comparisonError
      );
      return {
        control1Error: false,
        control2Error: false,
      };
    }
  }

  // get id value for autocomplete dropdowns
  public getAutocompleteDropdownId(
    dropdownResponse: any,
    formName: any,
    controlName: string,
    apiControlName: string,
    isForName?: boolean,
    formControlId?: string
  ) {
    // if block is for patching names to control by id(got from api)
    if (isForName) {
      const form = formName;
      const matchingData = dropdownResponse?.find(
        (item: any) => item.id === form.get(apiControlName)?.value
      );
      if (matchingData) {
        form.get(controlName)?.setValue(matchingData.name);
      }
    } else {
      this.getAutocompleteDropdownName(
        dropdownResponse,
        formName,
        controlName,
        apiControlName,
        'name',
        formControlId
      );
    }
  }

  public getAutocompleteDropdownIdParam(
    dropdownResponse: any,
    formName: any,
    controlName: string,
    apiControlName: string,
    formControlId?: string
  ) {
    this.getAutocompleteDropdownName(
      dropdownResponse,
      formName,
      controlName,
      apiControlName,
      'id',
      formControlId
    );
  }

  public getAutocompleteDropdownBinding(
    dropdownResponse: any[],
    formName: any,
    controlName: string,
    apiControlName: string,
    param: any
  ) {
    const form = formName;
    const formValue = form.get(controlName)?.value;
    let matchingData: any = null;
    if (lookupcontrolNames.includes(controlName)) {
      if (typeof formValue === DataTypes.String) {
        matchingData = this.findLocationByDisplayValue(
          formValue,
          dropdownResponse
        );
      }
    } else {
      matchingData = dropdownResponse.find(
        (item: any) => item[param] === formValue
      );
    }

    if (matchingData) {
      form.get(apiControlName)?.setValue(matchingData.id, { emitEvent: false });
      this.clearErrors(form, controlName);
    } else {
      form.get(controlName)?.setErrors({ autocompleteError: true });
    }
  }

  public getAutocompleteDropdownName(
    dropdownResponse: any[],
    formName: FormGroup,
    controlName: string,
    apiControlName: string,
    param: string,
    formControlId?: string
  ) {
    // Check if dropdownResponse is an array
    if (!Array.isArray(dropdownResponse)) return;
    let controlValue: string | null = formName.get(controlName)?.value;
    if (!controlValue) {
      if (apiControlName) {
        formName.get(apiControlName)?.setValue(null, { emitEvent: false });
      }
      this.clearErrors(formName, controlName);
    } else {
      controlValue = this.sanitizeControlValue(controlName, controlValue);
      const { exactMatch, potentialMatches } = this.findMatches(
        dropdownResponse,
        controlValue,
        param,
        controlName
      );

      if (exactMatch) {
        this.handleExactMatch(
          formName,
          exactMatch,
          controlName,
          apiControlName,
          param,
          formControlId
        );
      } else if (potentialMatches.length === 1) {
        this.handleSinglePotentialMatch(
          formName,
          potentialMatches[0],
          controlName,
          apiControlName,
          param,
          formControlId
        );
      } else {
        this.setAutocompleteError(formName, controlName);
      }
    }
  }

  private sanitizeControlValue(controlName: string, value: string): string {
    if (controlName === 'accountNumber') {
      return value.replace(/[\s-]/g, ''); // Remove spaces and dashes for account number
    } else if (controlName === 'timeZoneName') {
      return value.replace(/[_/\s-]/g, ''); // Remove spaces, backslash and dashes for account number
    }
    return value.replace(/\s/g, ''); // Remove spaces
  }

  private findMatches(
    dropdownResponse: any[],
    controlValue: string,
    param: string,
    controlName: string
  ) {
    let exactMatch: any = null;
    const potentialMatches: any[] = [];

    dropdownResponse.forEach((item: any) => {
      const dropdownOption = this.sanitizeControlValue(
        controlName,
        item[param]
      );

      if (dropdownOption.toLowerCase() === controlValue.toLowerCase()) {
        exactMatch = item;
      }

      if (dropdownOption.toLowerCase().includes(controlValue.toLowerCase())) {
        potentialMatches.push(item);
      }
    });

    return { exactMatch, potentialMatches };
  }

  private handleExactMatch(
    form: FormGroup,
    exactMatch: any,
    controlName: string,
    apiControlName: string,
    param: string,
    formControlId?: string
  ) {
    form.get(apiControlName)?.setValue(exactMatch.id, { emitEvent: false });
    form.get(controlName)?.patchValue(exactMatch[param], { emitEvent: false });
    this.clearErrors(form, controlName);
    this.preventFurtherTyping(form, controlName, exactMatch[param]);
    this.nextFieldFocusEvent(formControlId);
  }

  private handleSinglePotentialMatch(
    form: FormGroup,
    potentialMatch: any,
    controlName: string,
    apiControlName: string,
    param: string,
    formControlId?: string
  ) {
    form.get(controlName)?.patchValue(potentialMatch[param], { emitEvent: false });
    form.get(apiControlName)?.setValue(potentialMatch.id, { emitEvent: false });
    this.clearErrors(form, controlName);
    this.preventFurtherTyping(form, controlName, potentialMatch[param]);
    this.nextFieldFocusEvent(formControlId);
  }

  private clearErrors(form: FormGroup, controlName: string) {
    const control = form.get(controlName);
    if (!control) return;
    const currentErrors = control.errors;
    if (currentErrors) {
      const { autocompleteError, ...remainingErrors } = currentErrors;
      const hasRemaining = Object.keys(remainingErrors).length > 0;
      control.setErrors(hasRemaining ? remainingErrors : null, { emitEvent: false });
    }
  }

  private setAutocompleteError(form: FormGroup, controlName: string) {
    form.get(controlName)?.setErrors({ autocompleteError: true });
  }

  private preventFurtherTyping(
    form: FormGroup,
    controlName: string,
    exactMatchName: string
  ) {
    const control = form.get(controlName);
    if (control) {
      // Unsubscribe the previous subscription if exists
      if (this.preventFurtherTypingSubscription) {
        this.preventFurtherTypingSubscription?.unsubscribe();
      }
      // Listen for changes in the control value
      this.preventFurtherTypingSubscription = control.valueChanges.subscribe(
        value => {
          if (value !== exactMatchName) {
            const lastCharacter = value?.slice(-1);
            const allowedKeys = ['Backspace', 'Delete'];
            if (lastCharacter && !allowedKeys.includes(lastCharacter)) {
              control.setValue(exactMatchName, { emitEvent: false });
            } else if (value === '') {
              // Remove the exact match name if the field is cleared
              this.preventFurtherTypingSubscription?.unsubscribe();
              this.preventFurtherTypingSubscription = null;
            }
          }
        }
      );
    }
  }

  public downloadAttachment(
    data: string,
    isForFullmanFile: boolean
  ): Observable<any> {
    const url =
      (isForFullmanFile
        ? URLConstants.fullmanFileDownload
        : URLConstants.downloadAttachment) + data;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }

  public downloadUploadedFile(url: string): void {
    if (!url) {
      return;
    }
    const downloadLink = document.createElement('a');
    downloadLink.href = url;
    downloadLink.rel = 'noopener noreferrer';
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  downloadFile(url: string): Observable<Blob> {
    return this.http.get(url, { responseType: 'blob' });
  }

  onChangePage(event: any, pagingProp: any, getData: () => void) {
    pagingProp.fromIndex = event.pageIndex * event?.pageSize + 1;
    pagingProp.toIndex = pagingProp.fromIndex + (event?.pageSize - 1);
    getData();
  }

  isRowSelectionChange(params: RowSelectionChangeParams) {
    const {
      event,
      rowData,
      selection,
      totalData,
      getSummaryPageIconsWithState,
      manifestEnableView,
    } = params;
    if (event.target.checked) {
      params.selectedCount++;
      selection.select(rowData);
      if (params.selectedCount === totalData) {
        params.isAllCheckboxSelected = true;
      }
    } else {
      params.selectedCount--;
      selection.deselect(rowData);
      params.isAllCheckboxSelected = false;
    }
    if (selection.selected.length > 0) {
      getSummaryPageIconsWithState('Selected', true);
    } else {
      manifestEnableView(false);
    }
    return {
      selectedCount: params.selectedCount,
      isAllCheckboxSelected: params.isAllCheckboxSelected,
    };
  }

  handleCheckboxSelection(
    index: number,
    isSelected: boolean,
    gridColumns: any[],
    displayedColumns: string[],
    defaultGridDisplayColumns: string[]
  ) {
    if (!isSelected) {
      gridColumns[index].isSelected = true;
      displayedColumns.splice(0, 1); // to remove 'select' from array
      displayedColumns.splice(displayedColumns.length - 1, 1); // to remove 'action' from array
      displayedColumns.splice(index, 0, gridColumns[index].value);
      displayedColumns.unshift('select'); // to add 'select' as 1st position in array
      displayedColumns.push('action'); // to add 'action' as last position in array
      displayedColumns.sort(
        (a, b) =>
          defaultGridDisplayColumns.indexOf(a) -
          defaultGridDisplayColumns.indexOf(b)
      );
    } else {
      gridColumns[index].isSelected = false;
    }
  }

  public filterAndModifyIcons(
    icons: SvgIconProp[],
    condition: boolean
  ): SvgIconProp[] {
    const filtered = icons.filter((val: SvgIconProp) =>
      [
        ActionIcons.add,
        ActionIcons.view,
        ActionIcons.downloadFile,
        ActionIcons.print,
      ].includes(val.name)
    );

    return filtered.map((val: SvgIconProp) => {
      val.isDisabled = condition;
      return val;
    });
  }

  public setGridDisplayColumns(
    event: CdkDragDrop<string[]>,
    displayedColumns: string[],
    gridColumnConfig: TableColumns[],
    storageName: string
  ) {
    moveItemInArray(
      displayedColumns,
      event.previousIndex + 1,
      event.currentIndex + 1
    ); // in built method to reorder columns
    const unselectedColumns = gridColumnConfig.filter(
      column => !displayedColumns.includes(column.key)
    ); // filter out unselected columns
    gridColumnConfig = gridColumnConfig.filter(column =>
      displayedColumns.includes(column.key)
    );
    gridColumnConfig.sort(
      (a, b) =>
        displayedColumns.indexOf(a.key) - displayedColumns.indexOf(b.key)
    );
    gridColumnConfig.push(...unselectedColumns); // after sort adding unselected column at last
    const columnsOrderToSave = gridColumnConfig.map(obj => obj.key);
    columnsOrderToSave.unshift('select'); // to add 'select' as 1st position in array
    columnsOrderToSave.push('action'); // to add 'action' as last position in array
    localStorage.setItem(storageName, JSON.stringify(columnsOrderToSave));
    return gridColumnConfig;
  }

  public scrollToTop(elementRef: ElementRef): void {
    try {
      elementRef.nativeElement.scrollTop =
        elementRef.nativeElement.scrollHeight;
    } catch (err) {
      console.error(err);
    }
  }

  public setupFormIcons(
    formGroup: FormGroup,
    icons: any[],
    updateIconsState: (
      hasChanges: boolean,
      hasErrors: boolean,
      hasAnyValue: boolean
    ) => void
  ): void {
    const initialValue = formGroup.value;

    // Unsubscribe previous subscription if exists
    if (this.$subscription) {
      this.$subscription.unsubscribe();
    }

    // Combine valueChanges and statusChanges into a single subscription
    this.$subscription = combineLatest([
      formGroup.valueChanges,
      formGroup.statusChanges,
    ]).subscribe(([value, status]: [any, any]) => {
        const hasChanges = this.checkForFormChanges(initialValue, value);
        const hasErrors = formGroup.invalid;
        const hasAnyValue = this.checkForAnyValue(value);
        updateIconsState(hasChanges, hasErrors, hasAnyValue);
      });
  }

  private checkForFormChanges(initialValue: any, currentValue: any): boolean {
    return Object.keys(initialValue).some(
      key => currentValue[key] !== initialValue[key] && currentValue[key] !== ''
    );
  }

  private checkForAnyValue(value: any): boolean {
    return Object.values(value).some(val => val !== '' && val !== null);
  }

  // Optional: Implement a method to clean up subscriptions
  public unsubscribe(): void {
    if (this.$subscription) {
      this.$subscription.unsubscribe();
    }
  }

  public validateAndShowPopup(
    form: FormGroup,
    controlName: string,
    modalService: any,
    messageComponent: any,
    message: string
  ) {
    const control = form.get(controlName);
    if (control?.value && control.errors?.['autocompleteError']) {
      modalService.openPopup(
        {
          message: message,
          isFooterRequired: false,
        },
        ModalPopupMessages.Warning,
        messageComponent,
        '360px'
      );
    }
  }

  public scrollInfoToTop(element: ElementRef, smooth = true) {
    element.nativeElement.scrollTo({
      top: 0,
      behavior: smooth ? ScrollData.smooth : ScrollData.auto,
    });
  }

  public scrollInfoToBottom(element: ElementRef, smooth = true) {
    element.nativeElement.scrollTo({
      top: element.nativeElement.scrollHeight,
      behavior: smooth ? ScrollData.smooth : ScrollData.auto,
    });
  }

  public checkScrollPosition(element: ElementRef): boolean {
    const scrollPosition = element.nativeElement.scrollTop;
    const containerHeight = element.nativeElement.clientHeight;
    const contentHeight = element.nativeElement.scrollHeight;
    return scrollPosition + containerHeight >= contentHeight - 1;
  }

  public clearInputValue(data: any) {
    this.clearSelectionInputValue.next(data);
  }

  public bindBackValue(data: any) {
    this.formPatchback.next(data);
  }

  public getPreviousPage(data: string) {
    this.isPreviousPage.next(data);
  }

  public setData(data: any): void {
    this.paginationData.next(data);
  }

  public setBagData(data: any): void {
    this.bagPaginationData.next(data);
  }

  public afterFlightDelete(data: string) {
    this.isflightDelete.next(data);
  }

  public setSearchText(data: string) {
    this.patchSearchText.next(data);
  }

  public clearFieldError(form: FormGroup, fieldName: string, errorKey: string) {
    const control = form.get(fieldName);
    if (control?.hasError(errorKey)) {
      delete control.errors?.[errorKey];
      control.updateValueAndValidity();
    }
  }

  public getDataForDownloadSharedTabsInfoPage(
    allData: any,
    isForDownload: boolean
  ) {
    const attachmentsData = this.filterRequiredDataForAttachmentsDownloadFile(
      allData.attachmentList ? allData.attachmentList : allData.attachments
    );
    const sharedTabs: Record<string, SectionObject> = {
      notes: this.getSectionObject(
        !isForDownload &&
          !this.filteredNotesDataForDownloadFile(allData.notes).length
          ? [{}]
          : this.filteredNotesDataForDownloadFile(allData.notes),
        NotesHeaders.map((col: any) => col.label),
        NotesHeaders.map((col: any) => col.key),
        NotesHeaders
      ),
      events: this.getSectionObject([], [], [], []), // events are empty here
      attachments: this.getSectionObject(
        !isForDownload && !attachmentsData.length ? [{}] : attachmentsData,
        AttachmentsHeaders.map((col: any) => col.label),
        AttachmentsHeaders.map((col: any) => col.key),
        AttachmentsHeaders
      ),
    };
    // Filter out empty sections
    return Object.keys(sharedTabs).reduce(
      (result: Record<string, SectionObject>, key: string) => {
        if (sharedTabs[key]?.data?.length > 0) {
          result[key] = sharedTabs[key];
        }
        return result;
      },
      {}
    );
  }

  public filteredNotesDataForDownloadFile(incomingNotesData: any) {
    incomingNotesData.ibc.forEach((note: any) => {
      note.type = 'IBC';
    });
    incomingNotesData.customer.forEach((note: any) => {
      note.type = 'Customer';
    });
    const allNotes = [...incomingNotesData.ibc, ...incomingNotesData.customer];
    return allNotes.map((note: any) => {
      return {
        note: note?.comments ?? '',
        noteType: note?.type ?? '',
        createdBy: note?.userName ?? '',
        dateAndTime:
          this.datePipe.transform(
            note?.date,
            dateTimeFormat.dateTime24hoursWithSpace
          ) ?? '',
      };
    });
  }

  public filterRequiredDataForAttachmentsDownloadFile(
    incomingAttachmentsData: any
  ) {
    return incomingAttachmentsData.map((note: any) => {
      return {
        attachmentType: note?.fileType ?? '',
        fileName: note?.originalFileName ?? '',
        description: note?.description ?? '',
        createdBy: note?.userName ?? note?.username ?? '',
        dateAndTime:
          this.datePipe.transform(
            note?.createdDate,
            dateTimeFormat.dateTime24hoursWithSpace
          ) ??
          this.datePipe.transform(
            note?.date,
            dateTimeFormat.dateTime24hoursWithSpace
          ) ??
          '',
      };
    });
  }

  public getSectionObject(
    incomingData: any,
    incomingHeader: string[],
    incomingHeaderKeys: string[],
    columnConfig: any
  ) {
    return {
      data: incomingData,
      headers: incomingHeader,
      headerKeys: incomingHeaderKeys,
      config: columnConfig,
    };
  }

  public validateFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateFormFields(control);
      }
    });
  }

  public setControlRequiredValidation(form: any, controlName: string) {
    const formName = form;
    formName.get(controlName)?.addValidators(Validators.required);
    formName.get(controlName)?.updateValueAndValidity();
  }

  public removeControlRequiredValidation(form: any, controlName: string) {
    const formName = form;
    formName.get(controlName)?.removeValidators(Validators.required);
    formName.get(controlName)?.updateValueAndValidity();
  }

  public clearControlValidators(form: any, controlName: string) {
    const formName = form;
    formName.get(controlName)?.clearValidators();
    formName.get(controlName)?.updateValueAndValidity();
  }

  public markControlUnTouched(form: any, controlName: string) {
    const formName = form;
    formName.get(controlName)?.markAsUntouched();
  }

  public markControlTouched(form: any, controlName: string) {
    const formName = form;
    formName.get(controlName)?.markAsTouched();
  }

  public setPhoneControlValidation(
    form: any,
    controlName: string,
    isSpecificFormat: boolean
  ) {
    const formName = form;
    if (isSpecificFormat) {
      formName
        .get(controlName)
        ?.setValidators([
          Validators.pattern(ValidatorPattern.NANPPhoneValidation),
          Validators.minLength(12),
        ]);
    } else {
      formName
        .get(controlName)
        ?.addValidators([
          Validators.minLength(10),
          Validators.pattern(ValidatorPattern.alphaNumerics),
        ]);
    }
    formName.get(controlName)?.updateValueAndValidity();
  }

  public fileExtensionValidator(
    fileData: any,
    allowedExtensions: string[]
  ): ValidatorFn {
    return (control: AbstractControl) => {
      const fileName = control.value;
      if (fileName) {
        const fileExtension = fileData.name.split('.').pop()?.toLowerCase();
        if (allowedExtensions.includes(fileExtension || '')) {
          return null;
        } else {
          return { invalidExtension: true };
        }
      }
      return null;
    };
  }

  public fileSizeValidator(fileData: any, maxSizeMB: number): ValidatorFn {
    return (control: AbstractControl) => {
      const file = control.value as File | null;
      if (file) {
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        if (fileData.size > maxSizeBytes) {
          return { fileSize: true };
        }
      }
      return null;
    };
  }

  public setHawbIdFromHawbInformation(data: any) {
    this.getHawbIdFromHawbInformation.next(data);
  }

  public setMawbIdFromHawbInformation(data: any) {
    this.getMawbIdFromMawbInformation.next(data);
  }

  public addDeleteAttachmentCheck(data: any) {
    this.checkForCommodityAttachmentChanges.next(data);
  }

  public getPlaceholder(
    form: FormGroup,
    controlName: string,
    pageMode: string,
    modeIcons: any,
    bulkInfoData?: any
  ): string {
    const isBulkEdit = pageMode === modeIcons.bulkEdit;
    const controlValue = form.get(controlName)?.value;
    if (pageMode === modeIcons.bulkEdit && bulkInfoData) {
      if (bulkInfoData[controlName] === null) {
        return '';
      }
    }
    if (isBulkEdit && controlValue === '') {
      return LabelsAndPlaceHolders.variousValues;
    }
    return '';
  }

  public triggerAdvanceSearchSubmitAttempt(): void {
    this.advanceSearchSubmitAttempt.next();
  }

  public setRoutingFormValidity(isValid: boolean): void {
    this.routingFormValidity.next(isValid);
  }

  public isadvanceSearchFieldDirty(data: any) {
    this.checkAdvanceSearchFieldDirty.next(data);
  }

  public getPlaceholderForArrayControl(
    formArray: FormArray,
    index: number,
    controlName: string,
    pageMode: string,
    modeIcons: any
  ): string {
    if (index === 0) return '';
    const isBulkEdit = pageMode === modeIcons.bulkEdit;
    if (!isBulkEdit || !formArray) return '';

    const group = formArray.at(index) as FormGroup;
    const value = group?.get(controlName)?.value;

    const isEmpty =
      value === '' ||
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '');

    return isEmpty ? LabelsAndPlaceHolders.variousValues : '';
  }

  public calculateDateRange(selectedDate: string | Date): {
    minDate: Date;
    maxDate: Date;
  } {
    const baseDate = new Date(selectedDate);
    // Calculate min date (1 month before)
    const minYear = baseDate.getFullYear();
    const minMonth = baseDate.getMonth() - 1; // Subtract 1 month
    const minDay = baseDate.getDate();
    const minDate = new Date(minYear, minMonth, minDay);
    // Calculate max date (1 month after)
    const maxYear = baseDate.getFullYear();
    const maxMonth = baseDate.getMonth() + 1; // Add 1 month
    const maxDay = baseDate.getDate();
    const maxDate = new Date(maxYear, maxMonth, maxDay);
    return { minDate, maxDate };
  }

  public updateAdvanceState(defaultState: any, apiResponse: any): any {
    // Helper function to find and update keys in the nested structure
    function findAndUpdateKey(
      obj: any,
      keyToFind: string,
      valueToUpdate: any
    ): boolean {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          if (key === keyToFind) {
            obj[key] = valueToUpdate; // Update the value
            return true; // Key found and updated
          } else if (typeof obj[key] === 'object' && obj[key] !== null) {
            // Recursively search in nested objects
            const found = findAndUpdateKey(obj[key], keyToFind, valueToUpdate);
            if (found) {
              return true; // Stop once key is found and updated
            }
          }
        }
      }
      return false; // Key not found
    }

    // Loop through the API response keys
    for (const key in apiResponse) {
      if (Object.prototype.hasOwnProperty.call(apiResponse, key)) {
        if (Object.prototype.hasOwnProperty.call(defaultState, key)) {
          // If the key exists in the default state, update it directly
          if (
            typeof defaultState[key] === 'object' &&
            defaultState[key] !== null
          ) {
            defaultState[key] = this.updateAdvanceState(
              defaultState[key],
              apiResponse[key]
            );
          } else {
            defaultState[key] = apiResponse[key];
          }
        } else {
          // If the key doesn't exist at the top level, search for it in nested objects
          findAndUpdateKey(defaultState, key, apiResponse[key]);
        }
      }
    }
    return defaultState;
  }

  public getSearchCriteriaDisplay(payload: any): string {
    const criteria = { ...payload };
    delete criteria.userId;
    delete criteria.paging;
    return buildAdvanceSearchDisplayName(criteria);
  }

  public removeEmptyFields(obj: any): any {
    return Object.entries(obj)
      .filter(([_, value]) => {
        return (
          value !== '' &&
          value !== null &&
          value !== undefined &&
          !(Array.isArray(value) && value.length === 0) &&
          !(
            typeof value === 'object' &&
            !Array.isArray(value) &&
            Object.keys(this.removeEmptyFields(value)).length === 0
          )
        );
      })
      .reduce((acc: { [key: string]: any }, [key, value]) => {
        if (typeof value === 'object' && !Array.isArray(value)) {
          const cleanedObject = this.removeEmptyFields(value);
          if (Object.keys(cleanedObject).length > 0) {
            acc[key] = cleanedObject;
          }
        } else {
          acc[key] = value;
        }
        return acc;
      }, {});
  }

  public modifiedDataForPrint(
    hawbGridData: any,
    dataForPrint: any,
    isFrom: string,
    tableColumns: string[]
  ) {
    tableColumns.splice(0, 1); // to remove 'select' from array
    tableColumns.splice(tableColumns.length - 1, 1);
    const printData = {
      selectedData: hawbGridData,
      isFrom: isFrom,
      customizeColumns: JSON.parse(
        JSON.stringify(dataForPrint.selectedColumns)
      ),
      displayColumns: tableColumns,
      selectedPageSize: dataForPrint.selectedPageSize,
      isPrintDialogRequired: dataForPrint.isPrintDialogRequired,
      isPreviewWarning: dataForPrint.isPreviewWarning,
    };
    this.printService.setPrintTableData(printData);
    console.log(printData, 'set print data');
  }

  private nextFieldFocusEvent(formControlId?: string) {
    if (formControlId) {
      const tabEvent = tabKeyboardEvent;
      const focusedElement = document.activeElement;
      if (focusedElement) {
        focusedElement.dispatchEvent(tabEvent);
      }
      const nextInput = this.getNextInputElement(formControlId);
      if (nextInput) {
        (
          nextInput as
          | HTMLInputElement
          | HTMLTextAreaElement
          | HTMLSelectElement
        ).focus();
      }
    }
  }

  public getNextInputElement(formControlId?: string) {
    const inputs = Array.from(
      document.querySelectorAll('input, select, textarea')
    );
    const currentIndex = inputs.findIndex(
      input => input.getAttribute('id') === formControlId
    );

    for (let i = currentIndex + 1; i < inputs.length; i++) {
      if (
        !(
          inputs[i] as
          | HTMLInputElement
          | HTMLSelectElement
          | HTMLTextAreaElement
        ).disabled
      ) {
        return inputs[i];
      }
    }

    return null;
  }

  public attachmentFullManSignedUrl(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.signedFullManurl,
      data
    );
  }

  public generateUniqueTempId() {
    const timestamp = new Date().getTime().toString(36);
    const array = new Uint32Array(1);
    self.crypto.getRandomValues(array);
    return timestamp + '-' + array[0];
  }

  public fetchEventsData(dataEvent: any) {
    this.isSetEventsDataValue.next(dataEvent);
  }

  public validatePostalCode(postalCodeValue: string) {
    const parts = postalCodeValue.split('-');
    return (
      (parts.length === 2 && parts[1].length === 0) ||
      /^0*$/.test(postalCodeValue) ||
      /^[a-z]*$/i.test(postalCodeValue) ||
      (postalCodeValue.includes('-') &&
        (/[a-z]/i.test(parts[0]) || /[a-z]/i.test(parts[1]))) ||
      (postalCodeValue.includes('-') &&
        /^0*$/.test(parts[0]) &&
        /^0*$/.test(parts[1]))
    );
  }

  public preventTypingOrPasting(event: KeyboardEvent | ClipboardEvent): void {
    if (event instanceof KeyboardEvent) {
      if (!allowedKeys.includes(event.key)) {
        event.preventDefault();
      }
    } else {
      event.preventDefault();
    }
  }

  public isListAllResultClicked(data: boolean) {
    this.isListAllResultsData.next(data);
  }

  public removeExtraIcons(val: any) {
    return (
      val.name !== ActionIcons.bulkBag &&
      val.name !== ActionIcons.bulkGateway &&
      val.name !== ActionIcons.sendCbpClearance
    );
  }

  public processCustomsCityClearance(
    payload: CustomsCityProcessClearanceRequest
  ): Observable<CustomsCityProcessClearanceResponse> {
    return this.baseService
      .initiateApiRequest(
        HttpSignatures['Post'],
        URLConstants.processCustomsCityClearance,
        payload,
        '',
        'json'
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const body = parseCustomsCityClearanceResponse(error?.error);
          if (
            body?.statusCode ===
              CustomsCityClearanceStatusCodes.PartialQueued ||
            body?.statusCode === CustomsCityClearanceStatusCodes.NoneQueued
          ) {
            return of(body);
          }
          return throwError(() => error);
        })
      );
  }

  public validateCustomsCityClearance(
    payload: CustomsCityProcessClearanceRequest
  ): Observable<CustomsCityValidateCustomsCityClearanceResponse> {
    return this.baseService
      .initiateApiRequest(
        HttpSignatures['Post'],
        URLConstants.validateCustomsCityClearance,
        payload,
        '',
        'json'
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const body = parseCustomsCityValidateCustomsCityClearanceResponse(
            error?.error
          );
          if (
            body?.statusCode ===
              CustomsCityValidateChannelStatusCodes.PartialEligible ||
            body?.statusCode ===
              CustomsCityValidateChannelStatusCodes.NoneEligible
          ) {
            return of(body);
          }
          return throwError(() => error);
        })
      );
  }

  public validateClearanceSelection(
    payload: CustomsCityValidateClearanceSelectionRequest
  ): Observable<CustomsCityValidateClearanceSelectionResponse> {
    return this.baseService
      .initiateApiRequest(
        HttpSignatures['Post'],
        URLConstants.validateClearanceSelection,
        payload,
        '',
        'json'
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const body =
            error?.error as CustomsCityValidateClearanceSelectionResponse;
          if (
            body?.statusCode === CustomsCityClearanceStatusCodes.PartialQueued ||
            body?.statusCode === CustomsCityClearanceStatusCodes.NoneQueued
          ) {
            return of(body);
          }
          return throwError(() => error);
        })
      );
  }

  /* Legacy CBP - May 2026: Node routes commented; restore if rolling back from Customs City.
  public validateCBP(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.validateCBP,
      data,
      '',
      'json'
    );
  }

  public sentCBPClearance(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.clearanceCBP,
      data,
      '',
      'json'
    );
  }
  */

  public placeSendCbpClearanceAfterDelete(
    filtered: SvgIconProp[],
    allIcons: SvgIconProp[]
  ): SvgIconProp[] {
    const deleteIndex = filtered.findIndex(
      (icon: SvgIconProp) => icon.name === ActionIcons.delete
    );
    const sendCbpClearanceIcon = allIcons.find(
      (icon: SvgIconProp) => icon.name === ActionIcons.sendCbpClearance
    );

    if (deleteIndex !== -1 && sendCbpClearanceIcon) {
      // Remove sendCbpClearance if already present
      filtered = filtered.filter(
        (icon: SvgIconProp) => icon.name !== ActionIcons.sendCbpClearance
      );
      // Insert sendCbpClearance right after delete
      filtered.splice(deleteIndex + 1, 0, sendCbpClearanceIcon);
    }
    return filtered;
  }

  public createOutGoingBag(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.createOutGoingBag,
      data,
      '',
      'json'
    );
  }

  public createOutGoingManifest(data: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      HttpSignatures['Post'],
      URLConstants.createOutGoingManifest,
      data,
      '',
      'json'
    );
  }

  public hitAPIAfterCBPClearance(data: boolean) {
    this.ishitAPIAfterCBPClearance.next(data);
  }

  public triggerLoadMoreEvents(data: any) {
    this.isLoadMoreEventsTriggered.next(data);
  }

  public getPlacHolderTextForSBMM() {
    return (
      BasicSearchPlaceholder.searchByPlaceholder +
      BasicSearchPlaceholder.shipmentBagManifestMAWB
    );
  }

  public getTooltipInfoTextForSBMM() {
    return (
      BasicSearchPlaceholder.searchPerformedInfoText +
      BasicSearchPlaceholder.shipmentBagManifestMAWB +
      BasicSearchPlaceholder.infoText
    );
  }

  public getEventsList(
    requestType: string,
    id: any,
    fromIndex: number,
    toIndex: number
  ): Observable<any> {
    const url = `${URLConstants.getEventAllList}/${requestType}/${id}/${fromIndex}/${toIndex}`;
    return this.baseService.initiateApiRequest('GET', url, '', 'json');
  }

  public getSetDataValues(data: any) {
    this.isCheckDataValues.next(data);
  }

  public getModuleNames(isFrom: any, count: number) {
    if (isFrom === pageType.shipment) {
      return count === 1 ? 'Shipment' : 'Shipments';
    } else if (isFrom === pageType.bag) {
      return count === 1 ? 'Bag' : 'Bags';
    } else if (isFrom === pageType.manifest) {
      return count === 1 ? 'Manifest' : 'Manifests';
    } else {
      return count === 1 ? 'MAWB' : 'MAWBs';
    }
  }

  public transformRequestData(attachmentData: any): {
    requestType: string;
    isFromId: number;
  } {
    const mapRequestType = (type: string): string => {
      switch (type.toUpperCase()) {
        case 'MANIFEST':
          return 'manifest';
        case 'BAG':
          return 'bag';
        case 'MAWBS':
          return 'mawb';
        case 'HAWB':
          return 'hawb';
        case 'MAWB_FLIGHT':
          return 'flight';
        case 'HAWB_COMMODITIES':
          return 'commodity';
        default:
          return 'hawb';
      }
    };
    const mapFromIdKey = (type: string): string => {
      switch (type.toUpperCase()) {
        case 'MANIFEST':
          return 'manifestNumber';
        case 'BAG':
          return 'bagId';
        case 'MAWBS':
          return 'mawbId';
        case 'HAWB':
          return 'hawbId';
        default:
          return 'hawbId';
      }
    };
    const normalizedType = mapRequestType(attachmentData.requestType);
    const moduleId = localStorage.getItem(
      mapFromIdKey(attachmentData.requestType)
    );
    const isFromId =
      normalizedType === 'commodity' || normalizedType === 'flight'
        ? attachmentData.requestTypeIdInfo?.childId ?? moduleId
        : attachmentData.requestTypeIdInfo?.parentId ?? moduleId;
    return {
      requestType: normalizedType,
      isFromId,
    };
  }

  public shouldLoadMoreTimelineEvents(
    element: HTMLElement,
    scrollState: { lastScrollTop: number },
    threshold = 10
  ): boolean {
    const scrollTop = element.scrollTop;
    if (scrollTop === scrollState.lastScrollTop) {
      return false;
    }
    const scrollingDown = scrollTop > scrollState.lastScrollTop;
    scrollState.lastScrollTop = scrollTop;
    if (!scrollingDown) {
      return false;
    }
    return element.scrollHeight - scrollTop <= element.clientHeight + threshold;
  }

  public resetTimelineScrollState(scrollState: {
    lastScrollTop: number;
  }): void {
    scrollState.lastScrollTop = 0;
  }

  public loadTimelineEvents(
    moduleType: string,
    moduleId: string,
    currentList: any[],
    handleResponse: (response: any) => void,
    setLoading: (loading: boolean) => void,
    markAllLoaded: () => void
  ): void {
    const toIndex = currentList.length + 10;
    setLoading(true);
    this.getEventsList(moduleType, moduleId, 0, toIndex)
      .pipe(take(1), finalize(() => setLoading(false)))
      .subscribe({
        next: (response: any) => {
          if (response.eventList.length === currentList.length) {
            markAllLoaded();
            return;
          }
          handleResponse(response);
        },
        error: () => {
        },
      });
  }

  public selectedDataForBulkMenifesting(data: any) {
    this.isSelectedDataForBulkMenifest.next(data);
  }
  // bulk mawb
  public selectedDataForBulkMawb(data: any) {
    this.isSelectedDataForBulkMawb.next(data);
  }
  public mawbExistsForBulkValidate(inputValue: string) {
    const url = `${URLConstants.bulkMawbNumberValidate}/${inputValue}`;
    return this.baseService.initiateApiRequest('GET', url, inputValue, 'json');
  }

  public createMAWBForBulk(MAWBData: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'PUT',
      URLConstants.createBulkMAWB,
      MAWBData,
      '',
      'json'
    );
  }

  public getFromIndex(inputData: any) {
    let fromIndex = inputData.payload.paging.fromIndex;
    if (fromIndex <= 1) {
      fromIndex = 0;
    } else if (fromIndex % 5 === 1) {
      fromIndex = fromIndex - 1;
    } else if (fromIndex % 10 === 1) {
      fromIndex = fromIndex - 1;
    } else {
      fromIndex = Math.floor(fromIndex / 10) * 10;
    }
    return fromIndex;
  }

  public selectedAdvanceFacility = new BehaviorSubject<string[]>([]);
  public selectedGatewayFacility = new BehaviorSubject<number[]>([]);
  public selectedGatewayStatus = new BehaviorSubject<string>('');

  public setSelectedAdvanceFacility(facilityNames: string[]) {
    this.selectedAdvanceFacility.next(facilityNames);
  }

  public getSelectedAdvanceFacility(): string[] {
    return this.selectedAdvanceFacility.getValue();
  }

  public setSelectedGatewayFacility(facilityIds: number[]) {
    this.selectedGatewayFacility.next(facilityIds);
    if (!facilityIds?.length) {
      this.clearIsShipmentRoutedSelection();
    }
  }

  public getSelectedGatewayFacility(): number[] {
    return this.selectedGatewayFacility.getValue();
  }

  public setSelectedGatewayStatus(status: string) {
    this.selectedGatewayStatus.next(status);
  }

  public getSelectedGatewayStatus(): string {
    return this.selectedGatewayStatus.getValue();
  }

  public clearGatewaySelections(): void {
    this.selectedGatewayFacility.next([]);
    this.selectedGatewayStatus.next('');
    this.clearCbpHeldFilterSelection();
    this.clearIsShipmentRoutedSelection();
  }

  public setSelectedIsShipmentRouted(value: '' | 'true' | 'false'): void {
    this.selectedIsShipmentRouted.next(value);
  }

  public getSelectedIsShipmentRouted(): '' | 'true' | 'false' {
    return this.selectedIsShipmentRouted.getValue();
  }

  public clearIsShipmentRoutedSelection(): void {
    this.selectedIsShipmentRouted.next('');
  }

  public setSelectedCbpHeldFilter(value: '' | 'true' | 'false'): void {
    this.selectedCbpHeldFilter.next(value);
  }

  public getSelectedCbpHeldFilter(): '' | 'true' | 'false' {
    return this.selectedCbpHeldFilter.getValue();
  }

  public clearCbpHeldFilterSelection(): void {
    this.selectedCbpHeldFilter.next('');
  }

  public setSpecialSearchCheckboxFilter(
    fieldId: string,
    value: '' | 'true' | 'false'
  ): void {
    const current = { ...this.specialSearchCheckboxFilters.getValue() };
    if (value === '') {
      delete current[fieldId];
    } else {
      current[fieldId] = value;
    }
    this.specialSearchCheckboxFilters.next(current);
  }

  public getSpecialSearchCheckboxFilter(
    fieldId: string
  ): '' | 'true' | 'false' {
    return this.specialSearchCheckboxFilters.getValue()[fieldId] ?? '';
  }

  public getSpecialSearchCheckboxFilters(): Record<string, '' | 'true' | 'false'> {
    return this.specialSearchCheckboxFilters.getValue();
  }

  public clearSpecialSearchSelections(): void {
    this.specialSearchCheckboxFilters.next({});
    this.selectedSpecialSearches.next([]);
    this.selectedAdvanceFacility.next([]);
    this.clearGatewaySelections();
    this.clearGatewayBaggingSelection();
  }

  public selectedSpecialSearches = new BehaviorSubject<string[]>([]);

  public setSelectedSpecialSearches(checkboxIds: string[]) {
    this.selectedSpecialSearches.next(checkboxIds);
  }

  public getSelectedSpecialSearches(): string[] {
    return this.selectedSpecialSearches.getValue();
  }

  public getStatusCodeByModuleName(data: any) {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.getStatusDropdownBasedOnModule,
      data,
      'json'
    );
  }

  public setLastUsedPayload(payload: any): void {
    this._lastUsedPayload.next(payload);
  }

  public getLastUsedPayload(): any {
    return this._lastUsedPayload.getValue();
  }

  public clearLastUsedPayload(): void {
    this._lastUsedPayload.next(null);
  }

  public formatDateToYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  public restorePaginationAndSelection(
    paginationProps: any,
    summaryTableComp: any,
    isList?: boolean,
    restoreSelection = true
  ) {
    if (this.getLastUsedPayload()) {
      this.restorePagination(paginationProps, summaryTableComp, isList);
      if (restoreSelection) {
        summaryTableComp.restoreSelections();
      }
    }
  }

  public restorePagination(
    paginationProps: any,
    summaryTableComp: any,
    isList = false
  ) {
    if (paginationProps?.paging && summaryTableComp?.actualPaginator) {
      const fromIndex = paginationProps.paging.fromIndex;
      const toIndex = paginationProps.paging.toIndex;
      let pageSize;
      if (isList) {
        pageSize = toIndex - fromIndex;
      } else if (fromIndex === 0) {
        pageSize = toIndex - fromIndex;
      } else {
        pageSize = toIndex - fromIndex + 1;
      }

      if (pageSize <= 10) {
        pageSize = 10;
      } else if (pageSize <= 25) {
        pageSize = 25;
      } else {
        pageSize = 50;
      }

      summaryTableComp.actualPaginator.pageSize = pageSize;

      const pageIndex = Math.floor(fromIndex / pageSize);
      summaryTableComp.actualPaginator.pageIndex = pageIndex;
    }
  }

  public updatePublishButtonState(
    selectedRows: any[],
    chargesCountProperty: string
  ): { isPublishBtnDisable: boolean; isRecordsSelected: boolean } {
    let isPublishBtnDisable = true;
    if (
      (selectedRows.length === 1 &&
        selectedRows[0][chargesCountProperty] > 0) ||
      selectedRows.length > 1
    ) {
      isPublishBtnDisable = false;
    }
    const isRecordsSelected = selectedRows.length > 0;
    return {
      isPublishBtnDisable,
      isRecordsSelected,
    };
  }

  public selectedDataForBagScanShipment(data: any) {
    this.isSelectedDataForBagScanShipment.next(data);
  }

  public createBagShipmentFromSummary(manifestData: any): Observable<any> {
    return this.baseService.initiateApiRequest(
      'POST',
      URLConstants.createBulkBagScanShipment,
      manifestData,
      '',
      'json'
    );
  }

  public getLocationDetails(
    value: string | number | null | undefined,
    dataList: any[] = [],
    valueType: 'id' | 'name' = 'name'
  ) {
    if (value === null || value === undefined || value === '') {
      return { code: '', name: '', id: '' };
    }

    if (valueType === 'id') {
      const matchedById = dataList.find(
        (item: any) => Number(item.id) === Number(value)
      );
      return {
        code: matchedById?.code || '',
        name: matchedById?.name || '',
        id: matchedById?.id || '',
      };
    }

    const valueStr = String(value).trim();
    const matchedObject = this.findLocationByDisplayValue(valueStr, dataList);

    return {
      code: matchedObject?.code || valueStr || '',
      name: matchedObject?.name || valueStr || '',
      id: matchedObject?.id || '',
    };
  }

  /**
   * Resolve a facility from a display value (`code - name`, code-only, or name-only).
   * Must match code AND name when both are present — OR matching wrongly picks
   * the first facility that shares a code (e.g. JFK CFS vs JFK ECCF).
   */
  public findLocationByDisplayValue(
    value: string,
    dataList: any[] = []
  ): any | undefined {
    const valueStr = String(value ?? '').trim();
    if (!valueStr || !Array.isArray(dataList) || dataList.length === 0) {
      return undefined;
    }

    const normalized = valueStr.toLowerCase();

    const byFullDisplay = dataList.find(
      (item: any) =>
        this.formatLocationDisplay(item.code, item.name).toLowerCase() ===
        normalized
    );
    if (byFullDisplay) {
      return byFullDisplay;
    }

    const byExactCodeOrName = dataList.find(
      (item: any) =>
        String(item.code ?? '').toLowerCase() === normalized ||
        String(item.name ?? '').toLowerCase() === normalized
    );
    if (byExactCodeOrName) {
      return byExactCodeOrName;
    }

    if (!valueStr.includes(' - ')) {
      return undefined;
    }

    const separatorIndex = valueStr.indexOf(' - ');
    const codePart = valueStr.slice(0, separatorIndex).trim().toLowerCase();
    const namePart = valueStr.slice(separatorIndex + 3).trim().toLowerCase();

    return dataList.find(
      (item: any) =>
        String(item.code ?? '').toLowerCase() === codePart &&
        String(item.name ?? '').toLowerCase() === namePart
    );
  }

  public formatLocationDisplay(code?: string, name?: string): string {
    if (code && name) {
      return `${code} - ${name}`;
    } else if (name) {
      return name;
    } else {
      return code || '';
    }
  }

  public processLocationMappings(
    locationConfigs: Array<{
      key: string;
      id: string | number | null | undefined;
      dataSet: any[];
    }>
  ): Record<string, string> {
    const locationValues: Record<string, string> = {};
    for (const loc of locationConfigs) {
      const details = this.getLocationDetails(loc.id, loc.dataSet, 'id');
      const displayValue = this.formatLocationDisplay(
        details.code,
        details.name
      );
      locationValues[`${loc.key}Id`] = displayValue || '';
    }
    return locationValues;
  }

  public duplicatedRow(
    newRows: any[],
    existingRows: any[],
    keysToMatch: string[]
  ): any[] {
    const normalize = (val: any): any => {
      const str = String(val).trim();
      if (!val) return '';
      if (!Number.isNaN(Number(str))) {
        return Number(str);
      }
      return str;
    };
    return newRows.filter(nRow => {
      return existingRows.some(eRow => {
        let allMatch = true;
        keysToMatch.forEach(key => {
          const newRowVal = normalize(nRow[key]);
          const existingRowVal = normalize(eRow[key]);
          if (newRowVal !== existingRowVal) {
            allMatch = false;
          }
        });
        return allMatch;
      });
    });
  }

  public async handleDuplicateRows(
    modalService: any,
    commonService: any,
    candidateRows: any[],
    existingRows: any[],
    keyList: string[]
  ): Promise<{
    hasDuplicates: boolean;
    duplicateRows?: any[];
    popupResult?: DuplicatePopupResult;
  }> {
    const duplicates = commonService.duplicatedRow(
      candidateRows,
      existingRows,
      keyList
    );
    if (!duplicates || duplicates.length === 0) {
      return { hasDuplicates: false };
    }
    const duplicatesForPopup = duplicates.map((r: any, i: any) => {
      const originalIndex = candidateRows.indexOf(r);
      const assignedIndexedId = originalIndex >= 0 ? originalIndex : (r?.indexedId ?? i);
      return {
        ...r,
        indexedId: assignedIndexedId,
      };
    });

    const popupResult = await firstValueFrom<DuplicatePopupResult | undefined>(
      modalService.openDuplicatePopup(
        duplicatesForPopup,
        ModalPopupMessages.APShipmentDuplicateEntryMessage
      )
    );
    return {
      hasDuplicates: true,
      duplicateRows: duplicatesForPopup,
      popupResult,
    };
  }
  public setSelectedGatewayBaggingStatus(status: string): void {
    this.selectedGatewayBaggingStatus.next(status);
  }

  public getSelectedGatewayBaggingStatus(): string {
    return this.selectedGatewayBaggingStatus.getValue();
  }

  public clearGatewayBaggingSelection(): void {
    this.selectedGatewayBaggingStatus.next(DropdownSelectionValues.Select);
  }

  public normalizeWeightDimensionValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
    return '';
  }

  /** Blanks "null"/"undefined"/"NaN" parts of an L-W-H string; empty when no value remains. */
  public formatDimensionDisplay(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }
    const cleaned = value.replace(/\b(null|undefined|NaN)\b/gi, '');
    return /\d/.test(cleaned) ? cleaned : '';
  }

  public unknownToComparableString(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    switch (typeof value) {
      case 'object':
        return JSON.stringify(value);
      case 'string':
        return value;
      case 'number':
      case 'boolean':
      case 'bigint':
        return String(value);
      case 'symbol':
        return value.toString();
      case 'function':
        return value.toString();
      default:
        return '';
    }
  }
}
